import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../api/client";

const GENERIC_QUESTIONS = new Set([
  "What is your last name?",
  "What house are you in?",
]);

export default function EducatorSecuritySetup() {
  const navigate = useNavigate();
  const location = useLocation();

  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  // If status is already valid, send the user to the dashboard (or their intended page)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const status = await api.get("/api/auth/security-question/status/");
        if (!mounted) return;
        if (status?.ok) {
          const { has_security_question, is_generic, question: q } = status.data || {};
          const presetGeneric =
            is_generic === true || (typeof q === "string" && GENERIC_QUESTIONS.has(q.trim()));

          if (has_security_question && !presetGeneric) {
            const to = location.state?.from || "/dashboard";
            navigate(to, { replace: true });
          } else if (typeof q === "string" && q.trim()) {
            // Show existing (generic) question to nudge replacement
            setQuestion(q.trim());
          }
        }
      } catch {
        // If status fails, keep user here to allow setting it
      }
    })();
    return () => { mounted = false; };
  }, [navigate, location.state]);

  async function saveQA(e) {
    e.preventDefault();
    if (busy) return;

    const q = question.trim();
    const a = answer.trim();

    if (!q || !a) {
      setMsg("Both question and answer are required.");
      return;
    }

    if (GENERIC_QUESTIONS.has(q)) {
      setMsg("Please choose a more specific question (not a generic preset).");
      return;
    }

    setBusy(true);
    setMsg("");

    try {
      const res = await api.post("/api/auth/security-question/setup/", { question: q, answer: a });

      if (res?.ok) {
        const to = location.state?.from || "/dashboard";
        navigate(to, { replace: true });
        return;
      }

      const d = res?.data;
      setMsg(typeof d === "string" ? d : d?.detail || d?.message || "Could not save your security question.");
    } catch (err) {
      setMsg(err?.message || "Network error.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={styles.wrap}>
      <form onSubmit={saveQA} style={styles.card}>
        <h2 style={styles.h2}>Set Your Security Question</h2>

        <label style={styles.label} htmlFor="q">Question</label>
        <input
          id="q"
          style={styles.input}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="e.g., What is the name of your first pet?"
          disabled={busy}
          autoComplete="off"
        />

        <label style={styles.label} htmlFor="a">Answer</label>
        <input
          id="a"
          style={styles.input}
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          placeholder="Type your answer"
          disabled={busy}
          autoComplete="off"
        />

        <button type="submit" disabled={busy} style={styles.btn}>
          {busy ? "Saving…" : "Save and Continue"}
        </button>

        {msg && <div style={styles.msg}>{msg}</div>}

        <div style={styles.tip}>
          Tip: Pick something only you would know. Avoid the two generic presets:
          <ul style={styles.ul}>
            <li>“What is your last name?”</li>
            <li>“What house are you in?”</li>
          </ul>
        </div>
      </form>
    </div>
  );
}

const styles = {
  wrap: { minHeight: "100vh", display: "grid", placeItems: "center", background: "#F7F9FC", padding: 24 },
  card: {
    width: 460,
    maxWidth: "92vw",
    background: "#fff",
    padding: 24,
    borderRadius: 16,
    boxShadow: "0 10px 24px rgba(0,0,0,0.08)",
    display: "grid",
    gap: 10,
  },
  h2: { margin: 0, fontSize: 22, color: "#003e73", fontWeight: 800 },
  label: { fontWeight: 700, color: "#042d60", marginTop: 6 },
  input: {
    width: "100%",
    height: 44,
    borderRadius: 12,
    border: "1px solid rgba(0,0,0,0.12)",
    padding: "0 12px",
  },
  btn: {
    height: 44,
    borderRadius: 999,
    border: "none",
    background: "#f9a21a",
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer",
    marginTop: 6,
  },
  msg: { marginTop: 8, fontSize: 14, color: "#042d60" },
  tip: { marginTop: 6, fontSize: 12, color: "#6b7a86" },
  ul: { marginTop: 4, marginBottom: 0, paddingLeft: 18 },
};
